import { token } from '@atlaskit/tokens';
import { isUniqueViewName } from '@databases/api-views/utils/viewName';
import { isMobile } from '@databases/modes';
import { type DatabaseView } from '@databases/types/database/view';
import React, { type FunctionComponent, useEffect } from 'react';
import styled from 'styled-components';

interface ViewNameContainerProps {
  isActive?: boolean;
  isDefault: boolean;
  isViewActionsOpen: boolean;
  isReadonly?: boolean;
}

const getMaxWidth = (
  isViewActionsOpen: boolean,
  isDefault: boolean,
  isMobile: boolean
) => {
  const widths = isMobile
    ? {
        default: '130px',
        nonDefault: '190px',
        openDefault: '120px',
        openNonDefault: '160px',
      }
    : {
        default: '240px',
        nonDefault: '280px',
        openDefault: '190px',
        openNonDefault: '240px',
      };

  if (isViewActionsOpen) {
    return isDefault ? widths.openDefault : widths.openNonDefault;
  } else {
    return isDefault ? widths.default : widths.nonDefault;
  }
};

export const ViewNameContainer = styled.div<
  ViewNameContainerProps & { $maxWidth: string }
>`
  min-height: 20px;
  padding: ${token('space.025', '2px')} ${token('space.025', '2px')};
  border-radius: 2px;
  max-width: ${(props) => props.$maxWidth} !important;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export interface ViewCardNameEditProps {
  name: string;
  existingViewNames?: string[];
  view: DatabaseView;
  setDuplicateViewNameErrorId: (value: string) => void;
  validationProps: any;
  isActive?: boolean;
  isDefault: boolean;
  isViewActionsOpen: boolean;
  isReadonly?: boolean;
}

const ViewCardNameEdit: FunctionComponent<ViewCardNameEditProps> = ({
  name,
  existingViewNames = [],
  view,
  setDuplicateViewNameErrorId,
  isActive = false,
  isDefault,
  isViewActionsOpen,
  isReadonly,
}) => {
  const maxWidth = getMaxWidth(isViewActionsOpen, isDefault, isMobile());

  useEffect(() => {
    if (existingViewNames && !isUniqueViewName(view.name, existingViewNames)) {
      setDuplicateViewNameErrorId(view.id);
    } else {
      setDuplicateViewNameErrorId('');
    }
  }, [existingViewNames, setDuplicateViewNameErrorId, view.id, view.name]);

  return (
    <ViewNameContainer
      data-testid="view-name-container"
      isDefault={isDefault}
      isActive={isActive}
      isViewActionsOpen={isViewActionsOpen}
      isReadonly={isReadonly}
      $maxWidth={maxWidth}
    >
      {name}
    </ViewNameContainer>
  );
};

export default ViewCardNameEdit;
